"use client";

import { fadeUp, stagger } from "@/app/lib/utils/motionVariants";
import { motion } from "framer-motion";
import { Zap, Map, FileText, BarChart3 } from "lucide-react";
import Link from "next/link";
import { getLanguageFromPathname, getLocalizedRouteSegment, withLanguagePrefix } from "@/app/lib/i18n-routing";
import { iconMap } from "@/app/lib/iconMap";
import { usePathname } from "next/navigation";

type Props = {
    lang: string;
    resource:any
};

export default function ResourcesClient({ lang, resource }: Props) {
    const icons = [Map, Zap, FileText, BarChart3];
    const pathname = usePathname();
    const currentLanguage = getLanguageFromPathname(pathname);
    const sectionSegment = getLocalizedRouteSegment("resources", currentLanguage);
    const title = resource.title ?? "";
    const highlight = resource.titleHighlight?.trim();
    const highlightIndex = highlight
        ? title.toLowerCase().indexOf(highlight.toLowerCase())
        : -1;

    const items = [
        {
            title: "Blog",
            description: "Latest Insights & articles",
            slug: "/blogs",
        },
        {
            title: "Contact",
            description: "Get in touch with us",
            slug: "/contact",
        },
        {
            title: "FAQ",
            description: "Common Questions answered",
            slug: "/faq",
        },
    ];

    return (
        <>

            <section className="relative lg:min-h-[50vh] flex flex-col items-center justify-center">
                <div className="-translate-x-1/2 -z-1 absolute aspect-square left-1/2 max-w-full lg:max-w-[2560px] md:aspect-auto md:min-h-[500px] pointer-events-none top-0 w-full">
                    <div className='shadow-[0_0_120px_110px_rgb(36,91,187),0_0_220px_218px_rgb(36,91,187),0_0_790px_320px_rgb(36,91,187)] h-[0.1px] w-[0.1px] left-1/3 top-1/4 -translate-middle absolute rounded-full backdrop-blur-xl bg-[rgb(22,154,156)] scale-150 opacity-40'></div>
                    <div className="absolute -bottom-40 left-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 right-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full lg:w-[800px] h-[800px] bg-accent/10 rounded-full blur-3xl" />
                </div>
                <div className="container mx-auto relative z-10 pt-24 lg:pt-32 pb-4 md:pb-6 lg:pb-8">
                    <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto text-center">
                        <motion.h1 variants={fadeUp} className="banner-title">
                            {highlightIndex >= 0 && highlight ? (
                                <>
                                    {title.slice(0, highlightIndex)}
                                    <span className="banner-highlight-title">
                                        {title.slice(highlightIndex, highlightIndex + highlight.length)}
                                    </span>
                                    {title.slice(highlightIndex + highlight.length)}
                                </>
                            ) : (
                                title
                            )}
                        </motion.h1>
                        <motion.p variants={fadeUp} className="mt-3 banner-description">{resource.description}</motion.p>
                    </motion.div>
                </div>
            </section>

            <section className="py-6 lg:py-8">
                <div className="container mx-auto">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-8 mx-auto max-w-6xl">
                        {resource.items.map((item: any, i: number) => {
                            const routeSlug = item.localizedSlug ?? item.slug;
                            const LucideIcon =
                                iconMap[
                                item.icon as keyof typeof iconMap
                                ] as React.ElementType;

                            const Icon = LucideIcon || iconMap.Home;
                            // const Icon = icons[i];
                            return (
                                <Link key={i} href={withLanguagePrefix(`/${routeSlug}`, currentLanguage)} title={item.title}>
                                    <motion.div key={i} variants={fadeUp} className="relative overflow-hidden group rounded-2xl p-6 bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-xl lg:border-r lg:border-white/10">
                                        <div className='absolute top-0 right-0 group-hover:w-full group-hover:h-full  w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl -mr-10 -mt-10 transition-all group-hover:bg-cyan-500/15'></div>
                                        <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-5 border border-cyan-500/20 group-hover:bg-cyan-500/20 transition-colors">
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <h2 className="text-lg lg:text-xl font-bold text-white leading-none">{item.title}</h2>
                                        <p className="mt-2 text-xs md:text-sm text-white/70 font-medium">{item.subtitle}</p>
                                    </motion.div>
                                </Link>
                            );
                        })}
                    </motion.div>
                </div>
            </section>
        </>
    );
}